/**
 * Minimal Mercure subscriber presenting the same channel().listen() surface the
 * Vue components used with laravel-echo, so replacing Reverb required no
 * component changes: AuctionItem.vue still calls
 * `.channel(topic).listen(EventName, cb)` and `.leaveChannel(topic, cb)`.
 *
 * ONE EventSource is shared by every topic, and that is deliberate.
 *
 * Mercure's SSE frames carry only `id` and `data` — the topic is not
 * transmitted — so the obvious implementation is one connection per topic. That
 * does not survive this app: the auctions list mounts an AuctionItem per open
 * auction and each subscribes to its own `auction.{id}`. Over HTTP/1.1 those
 * long-lived connections exhaust the browser's ~6-per-origin budget, and the
 * next XHR or route change simply never gets a socket — the page silently stops
 * navigating. Dev and CI hit this because FrankenPHP serves plain HTTP there
 * ("HTTP/2 skipped because it requires TLS"), so there is no multiplexing to
 * hide it.
 *
 * Sharing one connection means we need the topic back, which is what the
 * `topic` key in each event's broadcastWith() supplies. Any new broadcast event
 * must include it (see App\Events\AuctionEvent), otherwise its messages fall
 * back to being delivered by event name to every topic listening for it.
  *
 * The hub keeps no history, so nothing published while the stream is down can
 * be replayed. Two things follow. A new topic opens the replacement stream
 * before the old one closes, so a bid landing mid-swap is not lost. And a
 * stream that drops is reopened with a backoff, after which onReconnect()
 * listeners are told so they can fetch what they missed.
 */
const CLOSED = 2;
const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000];

export default class Mercure {
  constructor() {
    /** @type {Object<string, Object<string, Function[]>>} topic -> event -> callbacks */
    this.listeners = {};
    /** @type {EventSource|null} the stream messages are taken from */
    this.source = null;
    /** @type {EventSource[]} streams kept open until their replacement is up */
    this.retiring = [];
    /** @type {Function[]} */
    this.reconnectListeners = [];
    this.reconnectTimer = null;
    this.retryTimer = null;
    this.failures = 0;
  }

  /**
   * @param {string} topic
   * @returns {{listen: (eventName: string, callback: Function) => object}}
   */
  channel(topic) {
    if (!this.listeners[topic]) {
      this.listeners[topic] = {};
      this.scheduleReconnect();
    }

    const events = this.listeners[topic];
    const api = {
      listen: (eventName, callback) => {
        // Written without ||= because .eslintrc.json pins ecmaVersion 11 (ES2020).
        if (!events[eventName]) {
          events[eventName] = [];
        }
        events[eventName].push(callback);
        return api;
      },
    };

    return api;
  }

  /**
   * Without a callback the whole topic goes. With one, only that callback
   * does, and the topic stays while anything else on the page listens to it.
   *
   * @param {string} topic
   * @param {Function} [callback]
   */
  leaveChannel(topic, callback) {
    const events = this.listeners[topic];
    if (!events) {
      return;
    }

    if (callback) {
      Object.keys(events).forEach((eventName) => {
        events[eventName] = events[eventName].filter((listener) => listener !== callback);
        if (!events[eventName].length) {
          delete events[eventName];
        }
      });
      if (Object.keys(events).length) {
        return;
      }
    }

    delete this.listeners[topic];
    this.scheduleReconnect();
  }

  /**
   * @param {Function} callback called each time the stream comes back after dropping
   * @returns {Function} stops the callback being called
   */
  onReconnect(callback) {
    this.reconnectListeners.push(callback);
    return () => {
      this.reconnectListeners = this.reconnectListeners.filter((listener) => listener !== callback);
    };
  }

  /**
   * Debounced so that a page mounting many AuctionItems at once results in a
   * single connection rather than one reconnect per card.
   */
  scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 50);
  }

  connect() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    if (this.source) {
      this.retiring.push(this.source);
      this.source = null;
    }

    const topics = Object.keys(this.listeners);
    if (!topics.length) {
      this.closeRetiring();
      return;
    }

    const url = new URL('/.well-known/mercure', window.location.origin);
    topics.forEach((topic) => url.searchParams.append('topic', topic));

    const source = new EventSource(url);
    source.addEventListener('open', () => {
      if (source !== this.source) {
        return;
      }
      this.closeRetiring();
      if (this.failures > 0) {
        this.failures = 0;
        this.reconnectListeners.forEach((listener) => listener());
      }
    });
    source.addEventListener('error', () => {
      if (source !== this.source) {
        return;
      }
      // The browser retries a stream that hiccups by itself, but nothing
      // published meanwhile reaches it, so that counts as a failure too. One
      // it has closed for good it leaves closed, so that is reopened here.
      this.failures += 1;
      if (source.readyState === CLOSED) {
        this.scheduleRetry();
      }
    });
    source.addEventListener('message', (message) => {
      // The broadcaster publishes {event, data}: the event's FQCN and the usual
      // Laravel broadcast payload built from the event's public properties.
      const payload = JSON.parse(message.data);
      const eventName = payload.event.replace(/^.*\\/, '');
      const data = payload.data || {};
      const { topic } = data;

      const targets = topic && this.listeners[topic]
        ? [topic]
        : Object.keys(this.listeners);

      targets.forEach((name) => {
        const callbacks = (this.listeners[name] || {})[eventName] || [];
        callbacks.forEach((callback) => callback(data));
      });
    });

    this.source = source;
  }

  scheduleRetry() {
    if (this.retryTimer) {
      return;
    }
    const delay = RETRY_DELAYS[Math.min(this.failures, RETRY_DELAYS.length) - 1];
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  closeRetiring() {
    this.retiring.forEach((retired) => retired.close());
    this.retiring = [];
  }
}
